import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import ResponsiveActions from "./ResponsiveActions";
import {
  Visibility,
  VisibilityOff,
  ContentCopy,
} from "@mui/icons-material";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import SavingsIcon from "@mui/icons-material/Savings";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

function TotalBalance() {
  const [balance, setBalance] = useState(null);
  const [displayBalance, setDisplayBalance] = useState(0);
  const [accountNumber, setAccountNumber] = useState("");
  const [error, setError] = useState("");
  const [showBalance, setShowBalance] = useState(false); // only for available
  const [snackbar, setSnackbar] = useState(false);
  const [user, setUser] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [showHint, setShowHint] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    axios
      .get("https://fentrixbank.org/api/api/user", { withCredentials: true })
      .then((res) => {
        const data = res.data;
        const bal = Number(data.current_balance ?? 0);
        setBalance(bal);

        // animated count up
        let start = 0;
        const duration = 800;
        const stepTime = 16;
        const increment = bal / (duration / stepTime);
        const counter = setInterval(() => {
          start += increment;
          if (start >= bal) {
            start = bal;
            clearInterval(counter);
          }
          setDisplayBalance(start);
        }, stepTime);

        setAccountNumber(
          data.account_number?.trim() || "Account number not active"
        );
        setUser(data);

        setAccounts([
          {
            key: "available",
            title: "Available Balance",
            balance: bal,
            number: data.account_number?.trim(),
            gradient: "linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)",
            icon: <CreditCardIcon />,
          },
          {
            key: "savings",
            title: "Savings",
            balance: Number(data.savings_balance ?? 0),
            number: "SA-" + (data.id || "0000"),
            gradient: "linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)",
            icon: <SavingsIcon />,
          },
          {
            key: "investments",
            title: "Investments",
            balance: Number(data.investment_balance ?? 0),
            number: "IV-" + (data.id || "0000"),
            gradient: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)",
            icon: <TrendingUpIcon />,
          },
        ]);
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
        setError("Failed to load account data");
      });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowHint(true);
      setTimeout(() => setShowHint(false), 2000);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const formatMoney = (n) =>
    Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

  const getMaskedNumber = (num) => {
    if (!num || num === "Account number not active")
      return "Account number not active";
    const digits = String(num).replace(/\s+/g, "");
    return "**** **** **** " + digits.slice(-4);
  };

  const handleCopy = (num) => {
    if (!num || num === "Account number not active") return;
    navigator.clipboard.writeText(num);
    setSnackbar(true);
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 2, sm: 3 },
        mt: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {showHint && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.5 }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1,
            color: "#666",
            fontSize: 14,
          }}
        >
          <TouchAppIcon sx={{ mr: 1, fontSize: 18 }} />
          Swipe to see more balances
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          overflowX: "auto",
          gap: 2,
          py: 2,
          px: 1,
          pr: 2, // ✅ prevent last card cutoff
          scrollSnapType: "x mandatory",
          "&::-webkit-scrollbar": { display: "none" },
          width: "113%",
          maxWidth: 1100,
        }}
      >
        {accounts.map((acc) => (
          <Card
            key={acc.key}
            component={motion.div}
            whileHover={{
              scale: 1.04,
              boxShadow: "0px 8px 20px rgba(0,0,0,0.15)",
            }}
            transition={{ duration: 0.25 }}
            sx={{
              background: acc.gradient,
              color: "#fff",
              borderRadius: 3,
              p: { xs: 3, sm: 3.5 },
              minWidth: { xs: "90%", sm: 320, md: 360 }, // ✅ responsive width
              minHeight: { xs: 180, sm: 200 }, // ✅ taller cards
              flex: "0 0 auto",
              scrollSnapAlign: "center", // ✅ better centering on swipe
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              textShadow: "0 1px 3px rgba(0,0,0,0.4)", // ✅ better contrast
            }}
          >
            {/* Top row: title + toggle/icon */}
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                {acc.title}
              </Typography>
              <Box display="flex" alignItems="center">
                {acc.key === "available" && (
                  <Tooltip title={showBalance ? "Hide balance" : "Show balance"}>
                    <IconButton
                      size="small"
                      onClick={() => setShowBalance(!showBalance)}
                      sx={{ color: "#fff", mr: 0.5 }}
                    >
                      {showBalance ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Tooltip>
                )}
                {acc.icon}
              </Box>
            </Box>

            {/* Balance */}
            <Typography
              fontWeight={800}
              sx={{ fontSize: { xs: "1.6rem", sm: "1.8rem", md: "2rem" }, mt: 1 }}
            >
              {acc.key === "available"
                ? showBalance
                  ? `$${formatMoney(displayBalance)}`
                  : "*****"
                : `$${formatMoney(acc.balance)}`}
            </Typography>

            {/* Account number row */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  letterSpacing: 2,
                  opacity: 0.95,
                }}
              >
                {getMaskedNumber(acc.number)}
              </Typography>
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={() => handleCopy(acc.number)}
                  sx={{ color: "#fff", ml: 1 }}
                  disabled={!acc.number}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Card holder */}
            <Typography
              variant="caption"
              sx={{ mt: 1.5, opacity: 0.9, display: "block" }}
            >
              {`Hi ${user?.username ?? "User"}, welcome back.`}
            </Typography>
          </Card>
        ))}
      </Box>

      <Box sx={{ width: "100%", maxWidth: 380, mt: 3 }}>
        <ResponsiveActions />
      </Box>

      {/* Snackbar with success Alert */}
      <Snackbar
        open={snackbar}
        autoHideDuration={2000}
        onClose={() => setSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          sx={{ width: "100%" }}
          onClose={() => setSnackbar(false)}
        >
          Account number copied!
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TotalBalance;